<template>
  <div>
    <div v-if="game">
      <div class="tw-fixed tw-flex tw-flex-col tw-inset-0 tw-bg-white tw-z-10">
        <div class="tw-relative tw-top-0 tw-left-0 tw-w-full tw-h-[68px]" />
        <iframe :src="game.url" class="tw-grow tw-w-full"></iframe>
      </div>
      <div
        class="tw-fixed tw-z-50 tw-top-0 tw-left-0 tw-w-full tw-h-[68px] 
          tw-bg-primary"
      >
        <button
          @click="onCloseActivityRequest"
          class="tw-block tw-absolute tw-top-2 tw-right-3 sf-btn tw-bg-mainbg1 
            tw-text-primary hover:tw-outline-primary"
        >
          CLOSE
        </button>
        <div
          class="tw-text-mainbg1 tw-text-center tw-py-[18px] tw-overflow-hidden 
            tw-text-xl"
        >
          {{ game.name }}
        </div>
      </div>
    </div>
    <div v-if="showActivityMain">
      <SfActivityMain @close="onCloseActivityRequest" />
    </div>
    <div v-else ref="mathTestUi" class="mathsui">
      <ModalDialog
        :show="mathProgress?.completed && mathResultsVisible"
        title="Test Completed"
        :prevent-body-scroll="false"
        @closed="closeMathResults"
      >
        <div class="sf-modal-body">
          <h3>Test Results</h3>
          <MathsTestResult :result="mathProgress" :student="$currentUser.value" />
        </div>
        <div class="sf-modal-footer">
          <button class="sf-btn-primary" @click.prevent="closeMathResults">
            Close
          </button>
        </div>
      </ModalDialog>
      <ModalDialog
        :show="mathQuestionVisible"
        :can-close="false"
        @closed="mathQuestionVisible = false"
        :prevent-body-scroll="false"
      >
        <div class="sf-modal-body">
          <div class="tw-text-center tw-mb-8">
            <RenderMathAnswer
              v-if="questionStep =='answer' &&
                answerResult &&
                answerResult.answerFeedback"
              :answer="answerResult.answer"
              :answer-render-kind="answerResult.answerFeedback.kind"
              :answer-render="answerResult.answerFeedback?.render?.correct"
              :question="mathCurrentQuestion"
              :formats="answerFormats"
              class="tw-mx-auto"
            />
            <RenderMathQuestion
              v-else
              :question-kind="mathCurrentQuestion.questionKind || 'text'"
              :question="mathCurrentQuestion.question || ''"
              :answer-fields="mathCurrentQuestion.answerFields"
              class="tw-mx-auto"
            />
          </div>
          <div v-if="questionStep == 'question'">
            <div>
              <MathAnswerForm
                :question="mathCurrentQuestion"
                :test-ui-element="mathTestUi"
                @submit="handleMathAnswer"
              />
            </div>
          </div>
          <div
            v-else-if="questionStep == 'answer'"
            class="tw-text-center tw-w-full"
          >
            <div v-if="answerResult" class="tw-flex tw-justify-center tw-gap-4">
              <div>
                <CheckSvg
                  v-if="answerResult.isCorrect"
                  :scale="1"
                  class="result-ok tw-block tw-mx-auto"
                />
                <XSvg
                  v-else
                  :scale="1"
                  class="result-wrong tw-block tw-mx-auto"
                />
              </div>
              <div>
                <div
                  :class="{
                    'maths-wrong-answer-box': !answerResult.isCorrect,
                    'maths-correct-answer-box': answerResult.isCorrect,
                  }"
                >
                  <div
                    class="tw-font-bold tw-mb-2"
                    :class="{
                      'tw-text-okcheck': answerResult.isCorrect,
                      'tw-text-danger': !answerResult.isCorrect,
                    }"
                  >
                    <div class="tw-text-center">
                      Your Answer
                    </div>
                  </div>
                  <RenderMathAnswer
                    :answer="answerResult.answer"
                    :answer-render-kind="answerResult.answerRender?.kind"
                    :answer-render="answerResult.answerRender?.render?.answer"
                    :question="mathCurrentQuestion"
                    :formats="answerFormats"
                  />
                </div>
              </div>
              <div>
                <div
                  v-if="!answerResult.isCorrect"
                  class="maths-correct-answer-box"
                >
                  <div
                    class="tw-text-center tw-font-bold tw-text-okcheck tw-mb-2"
                  >
                    Correct Answer
                  </div>
                  <RenderMathAnswer
                    :answer="answerResult.correctAnswer"
                    :answer-render-kind="answerResult.answerRender?.kind"
                    :answer-render="answerResult.answerRender?.render?.correct"
                    :question="mathCurrentQuestion"
                  />
                </div>
              </div>
            </div>
            <div ref="nextButton" class="tw-mt-4">
              <button @click="continueToNextQuestion" class="sf-btn-primary">
                Next
              </button>
            </div>
          </div>
        </div>
      </ModalDialog>
    </div>
    <ScoreSaveErrorHandler 
      :subject="isMaths ? 'maths' : 'spelling'" 
      @showmathresults="showMathResults"
      @mathsaved="onAnswerChecked"
    />
    <GameCloseConfirm
      :show="confirmCloseVisible"
      :is-maths="isMaths"
      @cancel="confirmCloseVisible = false"
      @close="closeActivity(); confirmCloseVisible = false;"
    />
  </div>
</template>

<style scoped lang="postcss">
.maths-answer-box {
  @apply tw-border-4 tw-p-2 tw-rounded-[0.8rem];
}
.maths-correct-answer-box {
  @apply maths-answer-box tw-border-okcheck;
}
.maths-wrong-answer-box {
  @apply maths-answer-box tw-border-danger;
}
</style>

<script lang="ts">
import { Dict, SfGame } from "@/types/generalTypes";
import {
  SfMathsAnswerResult,
  SfMathsCustomLevel,
  SfMathsLevel,
  SfMathsTestResult,
  SfMathTestQuestion,
} from "@/types/mathsTypes";
import { defineComponent, PropType } from "vue";
import MathsTestResult from "../school-shared/MathsTestResult.vue";
import ModalDialog from "../ui/ModalDialog.vue";
import {
  initMathsGameState,
  loadMathsTest,
  postMathsAnswer,
  mathGameProgress,
  clearMathsGame,
  mathGameCurrentQuestion,
  getNextQuestion,
  mathGameCurrentQuestionId,
} from "@/state/mathsGameState";
import {
  SfSpellingActivityInfo,
  SfSpellingTest,
  SfWordCategory,
  SfWordList,
} from "@/types/spellingTypes";
import {
  initSpellingGameState,
  spellingSaveGameData,
  spellingStartGame,
  spellingActivitySaveStatus,
  spellingGameStatus,
} from "@/state/spellingGameState";
import SfActivityMain from "./sf-activity/SfActivityMain.vue";
import RenderMathQuestion from "../shared/RenderMathQuestion.vue";
import MathAnswerForm from "./MathAnswerForm.vue";
import CheckSvg from "@/svg/CheckSvg.vue";
import XSvg from "@/svg/XSvg.vue";
import { preventScroll } from "@/state/appState";
import RenderMathAnswer from "../shared/RenderMathAnswer.vue";
import GameCloseConfirm from "./GameCloseConfirm.vue";
import { sfCurrentActivityScreen } from "@/state/sfActivityState";
import ScoreSaveErrorHandler from "./ScoreSaveErrorHandler.vue";

export default defineComponent({
  components: {
    ModalDialog,
    MathsTestResult,
    SfActivityMain,
    RenderMathQuestion,
    MathAnswerForm,
    CheckSvg,
    XSvg,
    RenderMathAnswer,
    GameCloseConfirm,
    ScoreSaveErrorHandler,
  },
  props: {
    game: Object as PropType<null | SfGame>,
    activityId: Number as PropType<null | number>,
    spellingRule: Object as PropType<SfWordCategory | null>,
    spellingTest: Object as PropType<SfSpellingTest | null>,
    wordList: Object as PropType<SfWordList | null>,
    trickyWords: Boolean,
    mathsLevel: Object as PropType<SfMathsLevel | null>,
    mathsCustomLevel: Object as PropType<SfMathsCustomLevel | null>,
  },
  emits: ["closegame", "closeactivity"],
  data() {
    return {
      questions: [] as SfMathTestQuestion[],
      mathResultsVisible: false,
      mathQuestionVisible: false,
      questionStep: "question" as "question" | "answer",
      answerResult: null as SfMathsAnswerResult | null,
      confirmCloseVisible: false,
    };
  },
  computed: {
    isMaths(): boolean {
      return this.mathsLevel != null || this.mathsCustomLevel != null;
    },
    mathProgress(): SfMathsTestResult | null {
      return mathGameProgress.value;
    },
    showActivityMain(): boolean {
      return (
        !this.isMaths &&
        (this.activityId == 1 || spellingActivitySaveStatus.value != 0)
      );
    },
    mathCurrentQuestion(): SfMathTestQuestion {
      return mathGameCurrentQuestion.value;
    },
    mathTestUi(): HTMLElement | null {
      return (this.$refs.mathTestUi as HTMLElement) || null;
    },
    answerFormats() {
      const res = {} as Dict<any>;
      if (
        this.answerResult?.correctAnswer &&
        Array.isArray(this.answerResult?.correctAnswer)
      ) {
        for (const a of this.answerResult.correctAnswer) {
          res[a.name] = a.fullFormat;
        }
      }
      return res;
    },
  },
  beforeUnmount() {
    (window as any).sfMathsLoadTest = null;
    (window as any).sfPostAnswer = null;
    (window as any).sfShowMathResults = null;
    (window as any).sfShowNextQuestion = null;
    (window as any).sfStartGame = null;
    (window as any).sfSaveData = null;
    preventScroll(false);
  },
  mounted() {
    if (this.isMaths) {
      (window as any).sfMathsLoadTest = loadMathsTest;
      (window as any).sfPostAnswer = postMathsAnswer;
      (window as any).sfShowMathResults = this.showMathResults;
      (window as any).sfShowNextQuestion = this.showNextMathQuestion;
    } else {
      (window as any).sfStartGame = spellingStartGame;
      (window as any).sfSaveData = spellingSaveGameData;
    }
  },
  methods: {
    showMathResults() {
      this.mathResultsVisible = true;
      this.mathQuestionVisible = false;
    },
    showNextMathQuestion() {
      getNextQuestion();
      this.questionStep = "question";
      this.mathQuestionVisible = true;
    },
    async handleMathAnswer(answer: string) {
      if (!this.mathProgress || !this.mathCurrentQuestion.question) {
        return;
      }
      const resData = await postMathsAnswer(
        this.mathProgress.id,
        mathGameCurrentQuestionId.value,
        this.mathCurrentQuestion,
        answer,
      );
      this.onAnswerChecked(resData as SfMathsAnswerResult);
    },
    onAnswerChecked(data: SfMathsAnswerResult) {
      this.answerResult = data;
      this.questionStep = "answer";
      this.$nextTick(() => {
        (this.$refs.nextButton as HTMLElement).scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      })
    },
    continueToNextQuestion() {
      (window as any).dispatchEvent(
        new CustomEvent("sfMathQuestionAnswered", {
          detail: this.answerResult,
        }),
      );
      this.mathQuestionVisible = false;
    },
    closeMathResults() {
      clearMathsGame();
      this.mathResultsVisible = false;
      (window as any).dispatchEvent(new CustomEvent("sfMathResultsClosed"));
    },
    initSfActivityInfo() {
      const info: SfSpellingActivityInfo = {
        activityId: this.activityId ? this.activityId : null,
        gameId: this.game ? this.game.id : null,
        wordCategoryId: this.spellingRule ? this.spellingRule.id : null,
        testId: this.spellingTest ? this.spellingTest.id : null,
        wordListId: this.wordList ? this.wordList.id : null,
        trickyWords: this.trickyWords,
      };
      initSpellingGameState(this.$currentUser.value, info);
    },
    isActivityInProgress() {
      if(this.showActivityMain) {
        return sfCurrentActivityScreen.value == 'game' 
          && spellingActivitySaveStatus.value < 2;
      } else if(this.game) {
        if(this.isMaths) {
          return mathGameProgress.value && !mathGameProgress.value?.completed
      } else 
        return spellingGameStatus.value == 'playing';
      }
      return false;
    },
    onCloseActivityRequest() {
      if(this.isActivityInProgress()) {
        this.confirmCloseVisible = true;
      } else {
        this.closeActivity();
      }
    },
    closeActivity() {
      this.questions = [];
      this.questionStep = "question";
      this.answerResult = null;
      this.mathQuestionVisible = false;
      this.mathResultsVisible = false;
      this.$emit("closegame");
      this.$emit("closeactivity");
    },
  },
  watch: {
    game() {
      if (this.game) {
        if (this.isMaths) {
          initMathsGameState(
            this.$currentUser.value,
            this.game.id,
            this.mathsLevel,
            this.mathsCustomLevel,
          );
        } else {
          this.initSfActivityInfo();
        }
        preventScroll(true);
      } else {
        preventScroll(false);
      }
    },
    activityId() {
      if (this.activityId && this.activityId > 0) {
        this.initSfActivityInfo();
      }
    },
  },
});
</script>
